import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  fameIndexColor,
  fameIndexLabelKey,
  fameIntegrityBand,
  showsTrendChart,
  type Brand,
} from '@fametrue/shared';
import {
  RISK_HEX,
  RISK_PILL_BG,
  RISK_PILL_FG,
  SIGNAL_QUALITY,
  formatDecimal,
  indexHex,
  indexPillBg,
  indexPillFg,
  integrityNavyColor,
  integrityPillBg,
  integrityPillFg,
  integrityRingColor,
} from './palette';
import { AnalysisPanel, EngravedGrid } from './AnalysisPanel';
import { BrandHeader } from './BrandHeader';
import { PLATFORM_NAMES, availablePlatforms } from './PlatformCards';
import { FadedRing, FirstCard, Ring, RiskGauge, SolidRing, TrendCard } from './visuals';

/**
 * Level 1 — Insight. Porting fedele di `renderLevel1()` dell'Allegato A.
 *
 * Due cose che il riferimento fa e che è facile "correggere" per sbaglio:
 * le card piattaforma NON stanno qui (sono nel Level 2) e le evidenze compaiono
 * solo come conteggio nel pannello di esito, mai come elenco.
 */
export function InsightView({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();

  const idx = brand.indices;
  const fr = brand.fameRisk;
  const history = brand.history.slice(-8);
  const firstSnapshot = history.length === 1 ? history[0] : undefined;

  const ws = idx.webScore;
  const fi = idx.fameIndex.value;
  const ft = idx.fameIntegrity;
  const band = fameIntegrityBand(ft);
  const signal = SIGNAL_QUALITY[brand.signalQuality] ?? SIGNAL_QUALITY['partial']!;

  return (
    <>
      <BrandHeader brand={brand} />

      <div className="sources-row">
        <span className="sources-label">{t('insight.sources')}</span>
        <SourcesRow brand={brand} />
        <div className="signal-quality" style={{ color: signal.color }}>
          <div className={`sq-dot ${signal.dot}`} />
          {t('insight.signalWithQuality', {
            quality: t(`signalQuality.${brand.signalQuality}`),
          })}
        </div>
      </div>

      <div className="kpi-split">
        <div>
          <div className="l2-section-title" style={{ marginBottom: 12 }}>
            {t('insight.indices')}
          </div>
          <div className="kpi-cards-left">
            {/* 1. WebScore — l'apparenza */}
            <div className="kpi-card webscore">
              {/* Le targhette erano `content:` in `fametrue.css`, quindi fuori
                  dalla localizzazione: restavano in italiano con l'interfaccia
                  in inglese (16/10, Level 1 punto 10). Lo `::before` è spento
                  da `app.css`; l'aspetto è lo stesso, byte per byte. */}
              <div className="kpi-plaque">{t('insight.plaqueAppearance')}</div>
              <div className="kpi-ring-wrap">
                <FadedRing value={ws} max={5} size={104} />
                <div className="kpi-ring-center">
                  <span className="kpi-value">{formatDecimal(ws, 2)}</span>
                  <span className="kpi-scale">/ 5</span>
                </div>
              </div>
              {idx.organicScore != null ? (
                <div className="kpi-organic">
                  {t('insight.organicScore')}: <strong>{formatDecimal(idx.organicScore, 2)}</strong>
                </div>
              ) : null}
              <KpiLabel
                prefix="Web"
                prefixColor="var(--muted)"
                suffix="Score"
                suffixColor="#9B9590"
                tooltip={t('insight.tipWeb')}
              />
            </div>

            {/* 2. FameIntegrity — l'affidabilità */}
            <div
              className={ft !== null && ft < 20 ? 'kpi-card integrity alarm' : 'kpi-card integrity'}
            >
              <div className="kpi-ring-wrap">
                <SolidRing
                  value={ft}
                  max={100}
                  color={integrityRingColor(ft)}
                  size={104}
                  gradientId="ring-integrity"
                />
                <div className="kpi-ring-center">
                  <span className="kpi-value" style={{ color: integrityRingColor(ft) }}>
                    {ft === null ? t('integrity.nd') : `${ft}%`}
                  </span>
                </div>
              </div>
              <div
                className="kpi-sublabel"
                style={{
                  background: integrityPillBg(ft),
                  color: integrityPillFg(ft),
                  borderColor: integrityPillBg(ft),
                }}
              >
                {t(`integrity.${band.labelKey}`)}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '8.5px',
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                  color: integrityPillFg(ft),
                  opacity: 0.7,
                  marginTop: 2,
                }}
              >
                {band.sublabelKey === 'nd' ? '' : t(`integritySublabel.${band.sublabelKey}`)}
              </div>
              <KpiLabel
                suffix="Integrity"
                suffixColor="#4A9EC5"
                tooltip={t('insight.tipIntegrity')}
              />
            </div>

            {/* 3. FameIndex — la realtà */}
            <div className="kpi-card fameindex">
              <div className="kpi-plaque">{t('insight.plaqueReality')}</div>
              <div className="kpi-ring-wrap">
                {fi != null ? (
                  <SolidRing
                    value={fi}
                    max={5}
                    color={fameIndexColor(fi)}
                    size={104}
                    gradientId="ring-index"
                  />
                ) : (
                  // Anello vuoto: nel riferimento l'opzione tratteggiata non
                  // ha effetto, si vede solo la traccia. Vedi Ring in visuals.
                  <Ring value={0} max={5} color="gray" size={104} stroke={7} />
                )}
                <div className="kpi-ring-center">
                  <span className="kpi-value" style={{ color: indexHex(fi) }}>
                    {fi != null ? formatDecimal(fi, 2) : t('integrity.nd')}
                  </span>
                  <span className="kpi-scale">
                    {fi != null ? '/ 5' : t('insight.notComputable')}
                  </span>
                </div>
              </div>
              <div
                className="kpi-sublabel"
                style={{
                  background: indexPillBg(fi),
                  color: indexPillFg(fi),
                  borderColor: indexPillBg(fi),
                }}
              >
                {t(`fameIndex.${fameIndexLabelKey(fi)}`)}
              </div>
              {/* v2.5: la motivazione del N.D. si legge in card, sotto la
                  pillola, come lo score spontaneo nella card WebScore. Con un
                  valore presente la nota si conserva e non si mostra. */}
              {fi == null && idx.fameIndex.note ? (
                <div className="kpi-note">{idx.fameIndex.note}</div>
              ) : null}
              <KpiLabel
                suffix="Index"
                suffixColor="var(--orange)"
                tooltip={t('insight.tipIndex')}
              />
            </div>

            {/* 4. FameRisk — l'esposizione */}
            <div className={`kpi-card famerisk ${riskClass(fr.level)}`}>
              <div className="kpi-gauge-wrap">
                <RiskGauge level={fr.level} />
              </div>
              {/* La forchetta della sanzione è stata rimossa su richiesta del
                  16/10 (Level 1, punto 3): al suo posto, sotto "Livello N",
                  sta la pillola dell'etichetta, che prima le stava accanto.
                  I due valori restano nel dato e si vedono nel Level 2. */}
              <div className="kpi-gauge-value" style={{ color: RISK_HEX[fr.level] }}>
                {t('insight.riskLevel')} {fr.level}
              </div>
              <div
                className="kpi-sublabel"
                style={{
                  background: RISK_PILL_BG[fr.level],
                  color: RISK_PILL_FG[fr.level],
                  borderColor: RISK_PILL_BG[fr.level],
                  // Lo stacco è quello che aveva la forchetta in questo slot:
                  // l'elemento cambia, la spaziatura resta.
                  marginTop: 4,
                }}
              >
                {t(`riskLevel.${fr.level}`)}
              </div>
              <KpiLabel suffix="Risk" suffixColor="#A32D2D" tooltip={t('insight.tipRisk')} />
            </div>
          </div>
        </div>

        {/* [YBD] Colonna a flex: l'analisi riempie lo spazio che resta sotto
            l'esito, così la colonna è alta quanto le card degli indici e non
            sfora sotto FameIndex/FameRisk. Il perché è in `AnalysisPanel`. */}
        <div className="kpi-split-right">
          <div className="l2-section-title" style={{ marginBottom: 12 }}>
            {t('insight.outcome')}
          </div>
          <VerdictPanel brand={brand} />

          <div className="l2-section-title" style={{ marginBottom: 12, marginTop: 16 }}>
            {t('insight.analysis')}
          </div>
          <AnalysisPanel brand={brand} />
        </div>
      </div>

      {/* Sotto i due snapshot il v40 non lascia più il vuoto: mostra i valori
          della prima rilevazione. La soglia di `showsTrendChart` resta invariata,
          cambia solo cosa si rende quando non è soddisfatta. */}
      {showsTrendChart(history.length) ? (
        <TrendSection brand={brand} history={history} />
      ) : firstSnapshot ? (
        <FirstSection brand={brand} snapshot={firstSnapshot} />
      ) : null}
    </>
  );
}

function riskClass(level: number): string {
  if (level <= 2) return 'low';
  if (level === 3) return 'medium';
  return '';
}

/**
 * Etichetta in fondo alla card KPI, a due parole e due colori.
 *
 * Il prefisso è "Fame" per i tre indici Fame, e il suo colore viene dal CSS
 * (`.fame-prefix`, navy). La card del WebScore passa invece due parole
 * proprie: dal 16/10 non si chiama più "FameWeb", e il blu resta il segno
 * degli indici Fame (feedback, Level 1 punto 1).
 */
function KpiLabel({
  prefix = 'Fame',
  prefixColor,
  suffix,
  suffixColor,
  tooltip,
}: {
  prefix?: string;
  /** Assente: vale il colore della regola CSS `.fame-prefix`. */
  prefixColor?: string;
  suffix: string;
  suffixColor: string;
  tooltip: string;
}): ReactNode {
  return (
    <div className="kpi-label-row kpi-label-bottom">
      <div className="kpi-label kpi-brand">
        <span className="fame-prefix" style={prefixColor ? { color: prefixColor } : undefined}>
          {prefix}
        </span>
        <span style={{ color: suffixColor }}>{suffix}</span>
      </div>
      <div className="kpi-info">
        i<div className="kpi-tooltip">{tooltip}</div>
      </div>
    </div>
  );
}

/**
 * Fonti analizzate. Il v39 elencava anche Reddit e Glassdoor come fonti future
 * spente: il v40 ha tolto quel blocco, quindi si mostrano solo le piattaforme
 * effettivamente presenti.
 *
 * Nel riferimento le voci sono figlie dirette di `.sources-row`. Il contenitore
 * serve solo sotto i 900 px, dove le fonti diventano una striscia scorrevole
 * sotto l'etichetta: su desktop `.sources-list` e' `display: contents` e sparisce
 * dal layout, quindi la resa resta quella del v40. Vedi app.css.
 */
function SourcesRow({ brand }: { brand: Brand }): ReactNode {
  const active = availablePlatforms(brand);

  return (
    <div className="sources-list">
      {active.map((p) => (
        <div className="source-item" key={p.id}>
          <div className="source-check on">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          {PLATFORM_NAMES[p.id] ?? p.name}
        </div>
      ))}
    </div>
  );
}

/** Tacche numeriche della scala FameIntegrity: le soglie di §6 più gli estremi. */
const SCALE_TICKS = [0, 20, 50, 80, 100] as const;
/** Distanza (in punti di scala) sotto cui una tacca finirebbe sotto l'ago. */
const NEEDLE_CLEARANCE = 6;

/** Targa navy con FameIntegrity, scala a fasce e conteggio evidenze. */
function VerdictPanel({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();
  const ft = brand.indices.fameIntegrity;
  const band = fameIntegrityBand(ft);
  const navy = integrityNavyColor(ft);
  const needleLeft = Math.max(0, Math.min(ft ?? 0, 100));

  return (
    <div className="kpi-verdict">
      <EngravedGrid />

      <div className="vp-int-head">
        <div
          className="vp-int-left"
          style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <div className="vp-brand">
            <span className="a">Fame</span>
            <span className="b-int">Integrity</span>
          </div>
          <div
            className="vp-pill"
            style={{
              background: 'rgba(255,139,122,0.14)',
              color: navy,
              border: '1px solid rgba(255,139,122,0.30)',
            }}
          >
            {t(`integrity.${band.labelKey}`)}
            {band.sublabelKey === 'nd' ? '' : ` · ${t(`integritySublabel.${band.sublabelKey}`)}`}
          </div>
        </div>
        <div className="vp-mega" style={{ color: navy }}>
          {ft ?? t('integrity.nd')}
          {ft === null ? null : <span className="pct">%</span>}
        </div>
      </div>

      {/* [YBD] Scala rivista su richiesta del committente: i numeri stanno
          sopra la barra (`.vp-scale-nums`, app.css). Il numero a ridosso
          dell'ago si nasconde, perché la punta dell'ago scende nella riga dei
          numeri e lo coprirebbe: il valore esatto lo dice già la targhetta.
          "Non Conforme" resta doppia come nel riferimento (una per fascia,
          rossa e arancione) su desktop; sotto i 900 px lo spazio non basta e
          compare una volta sola, centrata fra 0 e 50. Le due rese sono
          entrambe nel DOM e si scambiano in CSS. Divergenza dal v40. */}
      <div className="vp-scale-wrap">
        <div className="vp-scale-needle" style={{ left: `${needleLeft}%` }}>
          <div className="tag">{ft ?? '—'}</div>
          <div className="tri" />
        </div>
        <div className="vp-scale-nums">
          {SCALE_TICKS.map((n) => (
            <span
              key={n}
              className="sc-num"
              style={{ left: `${n}%` }}
              hidden={ft !== null && Math.abs(ft - n) <= NEEDLE_CLEARANCE}
            >
              {n}
            </span>
          ))}
        </div>
        <div className="vp-scale-bar">
          <div className="seg s1" />
          <div className="seg s2" />
          <div className="seg s3" />
          <div className="seg s4" />
        </div>
        <div className="vp-scale-combined">
          <span className="sc-lbl sc-lbl-wide" style={{ left: 0, width: '20%', color: '#FF8B7A' }}>
            {t('integrity.nonConforme')}
          </span>
          <span
            className="sc-lbl sc-lbl-wide"
            style={{ left: '20%', width: '30%', color: '#FFB088' }}
          >
            {t('integrity.nonConforme')}
          </span>
          <span
            className="sc-lbl sc-lbl-narrow"
            style={{ left: 0, width: '50%', color: '#FF8B7A' }}
          >
            {t('integrity.nonConforme')}
          </span>
          <span className="sc-lbl" style={{ left: '50%', width: '30%', color: '#FFCB6E' }}>
            {t('integrity.preCertificazioneShort')}
          </span>
          <span className="sc-lbl" style={{ left: '80%', width: '20%', color: '#6FDBC0' }}>
            {t('integrity.certificabileShort')}
          </span>
        </div>
      </div>

      {/* La riga con il nome del caso e il conteggio delle evidenze è stata
          rimossa su richiesta del 16/10 (Level 1, punto 2). Il pannello deve
          restare alto come prima, perché l'analisi qui sotto riempie lo spazio
          che resta nella colonna (`.kpi-split-right`) e altrimenti si
          allungherebbe: lo spazio della riga resta riservato in fondo.
          `evidenze` e `classification.casoName` non sono più renderizzati in
          alcun punto della piattaforma: restano validati e conservati. */}
      <div className="vp-freed" aria-hidden="true" />
    </div>
  );
}

function TrendSection({ brand, history }: { brand: Brand; history: Brand['history'] }): ReactNode {
  const { t } = useTranslation();
  const months = history.map((h) => h.month);
  const last = history[history.length - 1];
  if (!last) return null;

  const fi = brand.indices.fameIndex.value;
  const ft = brand.indices.fameIntegrity;

  return (
    <div className="l2-section" style={{ marginTop: 4 }}>
      <div className="l2-section-title">{t('insight.overTime')}</div>
      <div className="trend-row">
        <TrendCard
          // "WebScore" attaccato come FameIndex e FameIntegrity, e come lo
          // rende già la card KPI qui sopra (feedback 23/10, punto 18). Il v40
          // lo scrive staccato: divergenza chiesta dal committente.
          label="WebScore"
          value={formatDecimal(brand.indices.webScore, 2)}
          values={history.map((h) => h.webScore)}
          color="#6B6560"
          months={months}
        />
        <TrendCard
          label="FameIndex"
          value={fi != null ? formatDecimal(fi, 2) : t('integrity.nd')}
          values={fi != null ? history.map((h) => h.fameIndex) : null}
          color={indexHex(fi)}
          months={months}
        />
        <TrendCard
          label="FameIntegrity"
          value={ft === null ? t('integrity.nd') : `${ft}%`}
          values={history.map((h) => h.fameIntegrity)}
          color={integrityRingColor(ft)}
          decimals={0}
          months={months}
        />
      </div>
      <div className="trend-row">
        {/* Nel v40 questa card NON ha più il corpo ridotto: `trendCard()`
            confronta ancora l'etichetta con 'Volume' mentre ora si chiama
            'Volume medio', quindi la classe `small` non scatta più. È con ogni
            evidenza una svista del riferimento, replicata per conformità e
            segnalata al committente. */}
        <TrendCard
          label={t('insight.volume')}
          value={String(last.volume)}
          unit={t('insight.reviewsPerMonth')}
          values={history.map((h) => h.volume)}
          color="#142E4F"
          decimals={0}
          months={months}
          info={<Trans i18nKey="insight.tooltip.volume" />}
        />
        <TrendCard
          label={t('insight.perceptionGap')}
          value={last.gap != null ? formatDecimal(last.gap, 2) : t('integrity.nd')}
          unit={last.gap != null ? t('insight.points') : ''}
          values={last.gap != null ? history.map((h) => h.gap) : null}
          color={last.gap != null ? '#A32D2D' : '#9B9590'}
          months={months}
          info={
            <Trans i18nKey={last.gap != null ? 'insight.tooltip.gap' : 'insight.tooltip.gapNd'} />
          }
        />
        <TrendCard
          label={t('insight.replyRate')}
          value={String(last.replyRate)}
          unit="%"
          values={history.map((h) => h.replyRate)}
          color="#2A9D8F"
          decimals={0}
          months={months}
          info={<Trans i18nKey="insight.tooltip.replyRate" />}
        />
      </div>
    </div>
  );
}

/**
 * Ramo del v40 per il brand alla prima analisi: al posto degli andamenti si
 * mostrano i valori correnti, senza delta né sparkline. Nel v39 con un solo
 * snapshot non veniva reso nulla.
 */
function FirstSection({ brand, snapshot }: { brand: Brand; snapshot: Brand['history'][0] }) {
  const { t } = useTranslation();
  const fi = brand.indices.fameIndex.value;
  const ft = brand.indices.fameIntegrity;

  return (
    <div className="l2-section" style={{ marginTop: 4 }}>
      <div className="l2-section-title">{t('insight.firstReading')}</div>
      <div className="trend-row">
        <FirstCard label="WebScore" value={formatDecimal(brand.indices.webScore, 2)} />
        <FirstCard
          label="FameIndex"
          value={fi != null ? formatDecimal(fi, 2) : t('integrity.nd')}
        />
        <FirstCard
          label="FameIntegrity"
          value={ft === null ? t('integrity.nd') : String(ft)}
          unit="%"
        />
        <FirstCard
          label={t('insight.volume')}
          value={String(snapshot.volume)}
          unit={t('insight.reviewsPerMonth')}
          compact
          info={<Trans i18nKey="insight.tooltip.volumeShort" />}
        />
        <FirstCard
          label={t('insight.perceptionGap')}
          value={snapshot.gap != null ? formatDecimal(snapshot.gap, 2) : t('integrity.nd')}
          unit={snapshot.gap != null ? t('insight.points') : ''}
          info={<Trans i18nKey="insight.tooltip.gapShort" />}
        />
        <FirstCard
          label={t('insight.replyRate')}
          value={String(snapshot.replyRate)}
          unit="%"
          info={<Trans i18nKey="insight.tooltip.replyRate" />}
        />
      </div>
      <div className="first-note">{t('insight.firstReadingNote')}</div>
    </div>
  );
}
